import { z } from "zod";

import { OpaqueIdSchema, UsernameSchema } from "./identity-schemas";

const BlockIdSchema = OpaqueIdSchema;
const BlockTextSchema = z.string().max(10_000);
const ShortBlockTextSchema = z.string().max(1_000);

export const OpsDocBlockSchema = z.discriminatedUnion("type", [
  z.strictObject({ id: BlockIdSchema, type: z.literal("markdown"), text: z.string().max(100_000) }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("paragraph"), text: BlockTextSchema }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("heading-one"), text: ShortBlockTextSchema }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("heading-two"), text: ShortBlockTextSchema }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("bulleted-list"), text: BlockTextSchema }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("numbered-list"), text: BlockTextSchema }),
  z.strictObject({
    id: BlockIdSchema,
    type: z.literal("checklist"),
    text: BlockTextSchema,
    checked: z.boolean(),
  }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("quote"), text: BlockTextSchema }),
  z.strictObject({
    id: BlockIdSchema,
    type: z.literal("code"),
    text: BlockTextSchema,
    language: z.string().trim().max(40).default(""),
  }),
  z.strictObject({ id: BlockIdSchema, type: z.literal("divider") }),
]);
export type OpsDocBlock = z.infer<typeof OpsDocBlockSchema>;

export const OpsDocBlocksSchema = z
  .array(OpsDocBlockSchema)
  .min(1)
  .max(500)
  .refine((blocks) => new Set(blocks.map((block) => block.id)).size === blocks.length, {
    message: "Block identifiers must be unique.",
  });

export const OpsDocAccessModeSchema = z.enum(["everyone", "restricted", "private", "inherit"]);
export type OpsDocAccessMode = z.infer<typeof OpsDocAccessModeSchema>;
export const OpsDocAccessLevelSchema = z.enum(["view", "edit", "manage"]);
export type OpsDocAccessLevel = z.infer<typeof OpsDocAccessLevelSchema>;
export const OpsDocSearchStatusSchema = z.enum(["pending", "indexed", "failed"]);
export const OpsDocEntityTypeSchema = z.enum(["customer", "tool"]);
export type OpsDocEntityType = z.infer<typeof OpsDocEntityTypeSchema>;

export const OpsDocSpaceSchema = z.strictObject({
  id: OpaqueIdSchema,
  name: z.string().min(1).max(120),
  slug: z
    .string()
    .min(1)
    .max(80)
    .regex(/^[a-z0-9-]+$/),
  accessMode: z.enum(["everyone", "restricted"]),
  everyoneAccessLevel: z.enum(["view", "edit"]),
  accessLevel: OpsDocAccessLevelSchema,
  ownerEmployeeId: OpaqueIdSchema.nullable(),
  revision: z.number().int().positive(),
  createdAt: z.number().int().positive(),
  updatedAt: z.number().int().positive(),
});
export type OpsDocSpace = z.infer<typeof OpsDocSpaceSchema>;

export const OpsDocEntityLinkSchema = z.strictObject({
  entityType: OpsDocEntityTypeSchema,
  entityId: OpaqueIdSchema,
  label: z.string().min(1).max(200),
  customerId: OpaqueIdSchema,
  customerName: z.string().min(1).max(160),
});
export type OpsDocEntityLink = z.infer<typeof OpsDocEntityLinkSchema>;

export const OpsDocPageSummarySchema = z.strictObject({
  id: OpaqueIdSchema,
  spaceId: OpaqueIdSchema,
  parentPageId: OpaqueIdSchema.nullable(),
  title: z.string().min(1).max(200),
  accessMode: OpsDocAccessModeSchema,
  everyoneAccessLevel: z.enum(["view", "edit"]),
  accessLevel: OpsDocAccessLevelSchema,
  revision: z.number().int().positive(),
  searchStatus: OpsDocSearchStatusSchema,
  verifiedAt: z.iso.date().nullable(),
  nextVerificationAt: z.iso.date().nullable(),
  verificationStale: z.boolean(),
  updatedAt: z.number().int().positive(),
  links: z.array(OpsDocEntityLinkSchema).max(50),
});
export type OpsDocPageSummary = z.infer<typeof OpsDocPageSummarySchema>;

export const OpsDocAccessEntrySchema = z.strictObject({
  subjectType: z.enum(["employee", "group"]),
  subjectId: OpaqueIdSchema,
  label: z.string().min(1).max(160),
  accessLevel: OpsDocAccessLevelSchema,
});
export type OpsDocAccessEntry = z.infer<typeof OpsDocAccessEntrySchema>;

export const OpsDocPageSchema = OpsDocPageSummarySchema.extend({
  ownerEmployeeId: OpaqueIdSchema,
  ownerDisplayName: z.string().min(1).max(120),
  verifiedByEmployeeId: OpaqueIdSchema.nullable(),
  blocks: OpsDocBlocksSchema,
  accessEntries: z.array(OpsDocAccessEntrySchema).max(200),
  createdAt: z.number().int().positive(),
});
export type OpsDocPage = z.infer<typeof OpsDocPageSchema>;

export const OpsDocEmployeePrincipalSchema = z.strictObject({
  id: OpaqueIdSchema,
  displayName: z.string().min(1).max(120),
  username: UsernameSchema,
});
export type OpsDocEmployeePrincipal = z.infer<typeof OpsDocEmployeePrincipalSchema>;

export const OpsDocGroupSchema = z.strictObject({
  id: OpaqueIdSchema,
  name: z.string().min(1).max(120),
  description: z.string().max(300).nullable(),
  memberEmployeeIds: z.array(OpaqueIdSchema).max(500),
  updatedAt: z.number().int().positive(),
});
export type OpsDocGroup = z.infer<typeof OpsDocGroupSchema>;

export const OpsDocsBootstrapResponseSchema = z.strictObject({
  spaces: z.array(OpsDocSpaceSchema).max(100),
  pages: z.array(OpsDocPageSummarySchema).max(1_000),
  employees: z.array(OpsDocEmployeePrincipalSchema).max(500),
  groups: z.array(OpsDocGroupSchema).max(200),
  canWrite: z.boolean(),
  canManage: z.boolean(),
});

export const OpsDocSpaceCreateSchema = z.strictObject({
  name: z.string().trim().min(1).max(120),
});
export type OpsDocSpaceCreate = z.infer<typeof OpsDocSpaceCreateSchema>;
export const OpsDocSpaceResponseSchema = z.strictObject({ space: OpsDocSpaceSchema });

export const OpsDocPageCreateSchema = z.strictObject({
  spaceId: OpaqueIdSchema,
  parentPageId: OpaqueIdSchema.nullable().default(null),
  title: z.string().trim().min(1).max(200),
});
export type OpsDocPageCreate = z.infer<typeof OpsDocPageCreateSchema>;

export const OpsDocPageUpdateSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
  title: z.string().trim().min(1).max(200),
  blocks: OpsDocBlocksSchema,
  links: z
    .array(z.strictObject({ entityType: OpsDocEntityTypeSchema, entityId: OpaqueIdSchema }))
    .max(50)
    .refine(
      (links) =>
        new Set(links.map((link) => `${link.entityType}:${link.entityId}`)).size === links.length,
      "Page links must be unique.",
    ),
});
export type OpsDocPageUpdate = z.infer<typeof OpsDocPageUpdateSchema>;
export const OpsDocPageResponseSchema = z.strictObject({ page: OpsDocPageSchema });

export const OpsDocAccessUpdateSchema = z
  .strictObject({
    expectedRevision: z.number().int().positive(),
    accessMode: OpsDocAccessModeSchema,
    everyoneAccessLevel: z.enum(["view", "edit"]).default("view"),
    entries: z
      .array(
        z.strictObject({
          subjectType: z.enum(["employee", "group"]),
          subjectId: OpaqueIdSchema,
          accessLevel: OpsDocAccessLevelSchema,
        }),
      )
      .max(200),
  })
  .superRefine((value, context) => {
    const identities = value.entries.map((entry) => `${entry.subjectType}:${entry.subjectId}`);
    if (new Set(identities).size !== identities.length) {
      context.addIssue({
        code: "custom",
        path: ["entries"],
        message: "Access entries must be unique.",
      });
    }
    if (value.accessMode !== "restricted" && value.entries.length > 0) {
      context.addIssue({
        code: "custom",
        path: ["entries"],
        message: "Only restricted pages accept access entries.",
      });
    }
  });
export type OpsDocAccessUpdate = z.infer<typeof OpsDocAccessUpdateSchema>;

export const OpsDocGroupInputSchema = z.strictObject({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(300).nullable().default(null),
  memberEmployeeIds: z
    .array(OpaqueIdSchema)
    .max(500)
    .refine((ids) => new Set(ids).size === ids.length, "Group members must be unique."),
});
export type OpsDocGroupInput = z.infer<typeof OpsDocGroupInputSchema>;
export const OpsDocGroupResponseSchema = z.strictObject({ group: OpsDocGroupSchema });

export const OpsDocsSearchRequestSchema = z.strictObject({
  query: z.string().trim().min(2).max(500),
  limit: z.number().int().min(1).max(20).default(10),
});
export type OpsDocsSearchRequest = z.infer<typeof OpsDocsSearchRequestSchema>;

export const OpsDocsSearchResultSchema = z.strictObject({
  pageId: OpaqueIdSchema,
  title: z.string().min(1).max(200),
  path: z.string().min(1).max(1_000),
  snippet: z.string().max(1_000),
  score: z.number().min(0).max(1),
  updatedAt: z.number().int().positive(),
});
export type OpsDocsSearchResult = z.infer<typeof OpsDocsSearchResultSchema>;
export const OpsDocsSearchResponseSchema = z.strictObject({
  query: z.string().min(1).max(500),
  mode: z.enum(["semantic", "lexical"]),
  results: z.array(OpsDocsSearchResultSchema).max(20),
});

export const OpsDocsAskRequestSchema = z.strictObject({
  question: z.string().trim().min(2).max(1_000),
});
export type OpsDocsAskRequest = z.infer<typeof OpsDocsAskRequestSchema>;
export const OpsDocsAskResponseSchema = z.strictObject({
  answer: z.string().min(1).max(12_000),
  sources: z.array(OpsDocsSearchResultSchema).max(8),
});

export const OpsDocsLinkedQuerySchema = z.strictObject({
  entityType: OpsDocEntityTypeSchema,
  entityId: OpaqueIdSchema,
});
export const OpsDocsLinkedResponseSchema = z.strictObject({
  pages: z.array(OpsDocPageSummarySchema).max(100),
});

export const OpsDocLinkTargetSchema = z.strictObject({
  entityType: OpsDocEntityTypeSchema,
  entityId: OpaqueIdSchema,
  label: z.string().min(1).max(200),
  customerId: OpaqueIdSchema,
  customerName: z.string().min(1).max(160),
});
export type OpsDocLinkTarget = z.infer<typeof OpsDocLinkTargetSchema>;
export const OpsDocLinkTargetsResponseSchema = z.strictObject({
  targets: z.array(OpsDocLinkTargetSchema).max(1_000),
});

export const OpsDocArchiveRequestSchema = z.strictObject({
  expectedRevision: z.number().int().positive(),
});
export const OpsDocArchiveResponseSchema = z.strictObject({ archived: z.literal(true) });

export const OpsDocConflictResponseSchema = z.strictObject({
  currentRevision: z.number().int().positive(),
});

export const OpsDocRevisionSummarySchema = z.strictObject({
  revision: z.number().int().positive(),
  title: z.string().min(1).max(200),
  createdAt: z.number().int().positive(),
  author: z.string().min(1).max(120),
});
export const OpsDocHistoryResponseSchema = z.strictObject({
  revisions: z.array(OpsDocRevisionSummarySchema).max(50),
  nextBefore: z.number().int().positive().nullable(),
});
export const OpsDocRevisionResponseSchema = z.strictObject({
  revision: OpsDocRevisionSummarySchema.extend({ blocks: OpsDocBlocksSchema }),
});
